import { createSelector, createSlice } from '@reduxjs/toolkit';
import { get, isEqual, pick } from 'lodash';

import type { SearchDocumentItem } from '~/features/advanced-search/types';

import { toFilter } from '~/components/SearchV2/SearchV2.utils';
import { SEARCH_COUNT_SINGLE_VALUE_ID } from '~/components/Shared/Charts/constants';
import { SliceType } from '~/enums/SliceType';
import { FieldId } from '~/evifields/src/types/FieldId';
import { Filter } from '~/evifields/src/types/Filter';
import { buildFilterQuery } from '~/features/advanced-search/utils/build-query';
import { SearchItem } from '~/redux/api/evisearch/searchTypes';
import { Dashboard } from '~/redux/api/methods/dashboardV2';
import { toSearchV3Filters } from '~/redux/api/methods/searchV3';
import { Chart, ChartType, SearchFilter } from '~/types/DashboardV2';
import { DropdownFieldValue } from '~/types/Field';
import { getValidEntity } from '~/utils/object';
import { DEFAULT_TABLE_SORT_COLUMN_ORDER } from '~/utils/table';

import { getPersistedState } from '../../persist';
import { isActiveFilter } from '../search';
import {
  ColumnWidths,
  DashboardV2Slice,
  InitProps,
  KeyMapToPersist,
} from './types';

const name = SliceType.DashboardV2;

const keysToPersist: (keyof DashboardV2Slice)[] = ['columnWidths'];

const keyMapToPersist: KeyMapToPersist = keysToPersist.reduce<KeyMapToPersist>(
  (prev, curr) => {
    prev[curr] = [name, curr];
    return prev;
  },
  {},
);

const persistedState = {
  columnWidths: getPersistedState(keyMapToPersist.columnWidths!, {}),
};

export const getNonFilterCharts = (
  dashboard: Dashboard,
): Record<string, Chart> => ({
  [SEARCH_COUNT_SINGLE_VALUE_ID]: {
    id: SEARCH_COUNT_SINGLE_VALUE_ID,
    label: dashboard.name,
    section: 'General Information',
    type: 'single_value',
  },
});

export const initialState: DashboardV2Slice = {
  charts: [],
  columnOrder: [],
  currentChart: null,
  activeDashboard: {
    charts: [],
    columns: [],
    filters: [],
    id: '',
    name: '',
    query: [],
    is_default: false,
    creatorId: 0,
  },
  dashboardDirty: false,
  fieldValues: {},
  filterQuery: undefined,
  searchResultCache: {},
  intervals: {},
  chartWidths: {},
  page: 1,
  pageSize: 20,
  searchText: '',
  queryBuilder: undefined,
  queryBuilderPanelVisible: false,
  sortBy: DEFAULT_TABLE_SORT_COLUMN_ORDER,
  selectedFilters: [],
  crossFilters: [],
  ...persistedState,
};

const dashboardV2 = createSlice({
  name,
  initialState,
  selectors: {
    selectCharts: (state) => state.charts,
    selectColumnOrder: (state) => state.columnOrder,
    selectColumnWidths: (state) => state.columnWidths,
    selectCurrentChart: (state) => state.currentChart,
    selectActiveDashboard: (state) => state.activeDashboard,
    selectDashboardDirty: (state) => state.dashboardDirty,
    selectFieldValues: (state) => state.fieldValues,
    selectPage: (state) => state.page,
    selectPageSize: (state) => state.pageSize,
    selectSearchText: (state) => state.searchText,
    selectQueryBuilder: (state) => state.queryBuilder,
    selectFilterQuery: (state) => state.filterQuery,
    selectQueryBuilderPanelVisible: (state) => state.queryBuilderPanelVisible,
    selectSelectedFilters: (state) => state.selectedFilters,
    selectActiveFilters: createSelector(
      (state: DashboardV2Slice) => state.selectedFilters,
      (selectedFilters) => selectedFilters.filter(isActiveFilter),
    ),
    selectCrossFilters: (state) => state.crossFilters,
    selectSortBy: (state) => state.sortBy,
    selectIntervals: (state) => state.intervals,
    selectChartWidths: (state) => state.chartWidths,
    selectResultsCache: (state) => state.searchResultCache,
  },
  reducers: {
    deleteChart: (state, action) => {
      const index = state.charts.findIndex(
        (chart) => chart.id === action.payload.id,
      );
      state.charts.splice(index, 1);
      state.dashboardDirty = true;
    },
    initDashboard: (state, action: { payload: InitProps }) => {
      const { dashboard, searchFilters, tableSettings, defaultSortBy } =
        action.payload;

      const sortBy = defaultSortBy ?? DEFAULT_TABLE_SORT_COLUMN_ORDER;

      const { charts, columns = [], filters, query } = dashboard;

      const isComplexQuery = !!query.find((q) => q.type === 'section');

      // Mixing the non filter chats and the chart filters to create a record of all charts
      const allChartsRecord: Record<string, Chart> = {
        ...getNonFilterCharts(dashboard),
        ...Object.fromEntries(
          Object.entries(searchFilters).map(([key, value]) => [
            key,
            convertFilterToChart(value),
          ]),
        ),
      };

      // Mapping the charts record into the charts array repecting the charts sort order.
      const chartFilters: Chart[] = getValidEntity(
        allChartsRecord,
        charts.map((chart) => chart.filter_id),
      );

      // Mapping the saved type.
      const chartConfigByFilterId = Object.fromEntries(
        charts.map((chart) => [chart.filter_id, chart]),
      );
      state.charts = chartFilters.map((chart) => ({
        ...chart,
        type: chartConfigByFilterId[chart.id]?.type ?? chart.type,
      }));

      charts
        .filter((chart) => chart.interval)
        .forEach((chart) => {
          state.intervals[chart.filter_id] = chart.interval!;
        });

      charts
        .filter((chart) => chart.width)
        .forEach((chart) => {
          state.chartWidths[chart.filter_id] = chart.width!;
        });

      const defaultFilters: Array<Filter> = [];
      filters.forEach((defaultFilter) => {
        const searchFilter = Object.values(searchFilters).find(
          (searchFilter) => searchFilter.id.toString() === defaultFilter,
        );
        if (searchFilter) {
          defaultFilters.push(toFilter(searchFilter.id));
        }
      });
      const providedFiltersAndValues = isComplexQuery
        ? []
        : toSearchV3Filters(query, searchFilters);

      const isDifferentDashboard = state.activeDashboard?.id !== dashboard?.id;

      state.activeDashboard = dashboard;
      state.selectedFilters =
        state.selectedFilters.length && !isDifferentDashboard
          ? state.selectedFilters
          : [...providedFiltersAndValues, ...defaultFilters];
      state.crossFilters = [];
      state.queryBuilder = isComplexQuery ? query : undefined;
      state.filterQuery = [
        ...query,
        ...buildFilterQuery({
          filters: [...defaultFilters],
          searchFilters,
        }),
      ];
      state.page = 1;
      state.dashboardDirty = false;
      state.columnOrder = columns;
      state.searchText = isDifferentDashboard ? '' : state.searchText;
      if (dashboard.is_default) {
        state.pageSize = 20;
        state.sortBy = sortBy;
      } else {
        state.columnWidths = tableSettings.columnWidths;
        state.pageSize = tableSettings.pageSize;
        state.sortBy = tableSettings.sortBy;
        if (!isEqual(tableSettings.columnOrder, dashboard.columns)) {
          state.dashboardDirty = true;
        }
      }
    },
    patchColumnWidths: (state, action: { payload: ColumnWidths }) => {
      state.columnWidths = {
        ...state.columnWidths,
        ...action.payload,
      };
    },
    setColumnOrder: (state, action) => {
      state.columnOrder = action.payload;
    },
    setCurrentChart: (state, action) => {
      state.currentChart = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setChartInterval: (state, { payload }) => {
      state.intervals[payload.id] = payload.value;
      state.dashboardDirty = true;
    },
    setChartWidth: (state, { payload }) => {
      state.chartWidths[payload.id] = payload.value;
      state.dashboardDirty = true;
    },
    setChartType: (state, { payload }) => {
      state.charts = state.charts.map((chart) => {
        if (chart.id === payload.id) {
          return {
            ...chart,
            type: payload.value,
          };
        }
        return chart;
      });
      state.dashboardDirty = true;
    },
    setChartFilters: (state, action) => {
      state.charts = action.payload;
    },
    setDashboardDirty: (state, action) => {
      state.dashboardDirty = action.payload;
    },
    setFieldValues: (
      state,
      {
        payload,
      }: { payload: { fieldId: string; values: DropdownFieldValue[] } },
    ) => {
      state.fieldValues[payload.fieldId] = payload.values;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setSearchText: (state, { payload }) => {
      state.searchText = payload;
    },
    setQueryBuilder: (state, action) => {
      state.queryBuilder = action.payload;
    },
    setFiltersQuery: (state, action) => {
      state.filterQuery = action.payload;
    },
    setQueryBuilderPanelVisible: (state, action) => {
      state.queryBuilderPanelVisible = action.payload;
    },
    setSelectedFilters: (state, action) => {
      state.selectedFilters = action.payload;
    },
    setCrossFilters: (state, action) => {
      state.crossFilters = action.payload;
    },
    setSortBy: (state, action) => {
      const newSortBy = { ...action.payload };
      state.sortBy = newSortBy;
    },
    resetFilters: (
      state,
      action: { payload: { searchFilters: Record<FieldId, SearchFilter> } },
    ) => {
      const { filters, query } = state.activeDashboard;
      const { searchFilters } = action.payload;
      const isComplexQuery = !!query.find((q) => q.type === 'section');
      const defaultFilters: Array<Filter> = [];

      filters.forEach((defaultFilter) => {
        const searchFilter = Object.values(searchFilters).find(
          (searchFilter) => searchFilter.id.toString() === defaultFilter,
        );
        if (searchFilter) {
          defaultFilters.push(toFilter(searchFilter.id));
        }
      });
      const providedFiltersAndValues = isComplexQuery
        ? []
        : toSearchV3Filters(query, searchFilters);
      state.filterQuery = [
        ...query,
        ...buildFilterQuery({
          filters: [...defaultFilters],
          searchFilters,
        }),
      ];
      state.selectedFilters = [...providedFiltersAndValues, ...defaultFilters];
      state.crossFilters = [];
    },
    patchSearchResultsCache: (
      state,
      { payload = [] }: { payload: SearchDocumentItem[] },
    ) => {
      payload.forEach((item) => (state.searchResultCache[item.id] = item));
    },
    patchEvisearchResultsCache: (
      state,
      { payload = [] }: { payload: SearchItem[] },
    ) => {
      payload.forEach(
        (item) =>
          (state.searchResultCache[item.id] = {
            ...item,
            document_handlers: get(
              item,
              'selected_field_values.document_handler_id_Document.value_structured',
              [],
            ),
            full_text_matches: item.highlights?.full_text_matches ?? [],
            // TODO: Map types correctly once the API gets formalized
            permissions: item.permissions as any,
            selected_field_values: item.selected_field_values as any,
            id: item.id as any,
          }),
      );
    },

    reset: (state) => {
      return {
        ...initialState,
        ...pick(state, ['searchResultCache']),
        filters: state.selectedFilters,
      };
    },
  },
});

export function convertFilterTypeTochartType(filter: SearchFilter): ChartType {
  switch (filter.type) {
    case 'boolean':
    case 'enum':
      return 'donut';
    case 'age':
    case 'date':
    case 'number':
      return 'vertical_bar';
    default:
      return 'horizontal_bar';
  }
}

export const convertFilterToChart = (searchFilter: SearchFilter) => ({
  id: searchFilter.id,
  label: searchFilter.label,
  section: searchFilter.section,
  type: convertFilterTypeTochartType(searchFilter),
  filterType: searchFilter.type,
});

export default dashboardV2;
